import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  cancelMigrationJob,
  clearActiveMigrationJobId,
  formatValidationError,
  getMigrationJob,
  isMigrationJobInProgress,
  loadActiveMigrationJobId,
  resumeMigrationImport,
  saveActiveMigrationJobId,
  startMigrationImport,
  startMigrationValidate,
  uploadMigrationFile,
  validationRowCount,
  type MigrationImportOptions,
  type MigrationJob,
  type MigrationJobType,
  type MigrationValidationError,
} from '@/lib/api/migration.js';
import { useAuthStore } from '@/stores/auth.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { cn } from '@/lib/utils.js';

type TabId = 'inventory' | 'services' | 'customers' | 'tickets' | 'invoices';

type MigrationStepsState = {
  products: boolean;
  services: boolean;
  customers: boolean;
  tickets: boolean;
  invoices: boolean;
};

const MIGRATION_STEPS_KEY = 'repairiz_migration_steps';
const TICKET_PREFIX_KEY = 'repairiz_migration_ticket_prefix';
const SALE_PREFIX_KEY = 'repairiz_migration_sale_prefix';

const DEFAULT_STEPS: MigrationStepsState = {
  products: false,
  services: false,
  customers: false,
  tickets: false,
  invoices: false,
};

function loadSavedTicketPrefix(): string {
  try {
    return localStorage.getItem(TICKET_PREFIX_KEY)?.trim() || 'RD-';
  } catch {
    return 'RD-';
  }
}

function saveTicketPrefix(prefix: string): void {
  try {
    localStorage.setItem(TICKET_PREFIX_KEY, prefix.trim() || 'RD-');
  } catch {
    /* ignore */
  }
}

function loadSavedSalePrefix(): string {
  try {
    return localStorage.getItem(SALE_PREFIX_KEY)?.trim() || 'INV-';
  } catch {
    return 'INV-';
  }
}

function saveSalePrefix(prefix: string): void {
  try {
    localStorage.setItem(SALE_PREFIX_KEY, prefix.trim() || 'INV-');
  } catch {
    /* ignore */
  }
}

function loadMigrationSteps(): MigrationStepsState {
  try {
    const raw = window.localStorage.getItem(MIGRATION_STEPS_KEY);
    if (!raw) return { ...DEFAULT_STEPS };
    return { ...DEFAULT_STEPS, ...(JSON.parse(raw) as Partial<MigrationStepsState>) };
  } catch {
    return { ...DEFAULT_STEPS };
  }
}

function saveMigrationSteps(patch: Partial<MigrationStepsState>): MigrationStepsState {
  const next = { ...loadMigrationSteps(), ...patch };
  window.localStorage.setItem(MIGRATION_STEPS_KEY, JSON.stringify(next));
  return next;
}

const POLL_MS = 2_000;
const MAX_STALL_POLLS = 300; // 10 min with no processedRows change
const UPLOAD_TIMEOUT_MS = 30_000;

const TABS: Array<{
  id: TabId;
  label: string;
  jobType: MigrationJobType;
  blurb: string;
  stepKey: keyof MigrationStepsState;
  icon: string;
}> = [
  {
    id: 'inventory',
    label: 'Products',
    jobType: 'inventory',
    blurb: 'Parts & products from RepairDesk export',
    stepKey: 'products',
    icon: '📦',
  },
  {
    id: 'services',
    label: 'Services',
    jobType: 'services',
    blurb: 'Repair services from RepairDesk export',
    stepKey: 'services',
    icon: '🔧',
  },
  {
    id: 'customers',
    label: 'Customers',
    jobType: 'customers',
    blurb: 'Customer records (supports ~80k rows)',
    stepKey: 'customers',
    icon: '👥',
  },
  {
    id: 'tickets',
    label: 'Tickets',
    jobType: 'ticket_history',
    blurb: 'Closed ticket history grouped by Ticket ID',
    stepKey: 'tickets',
    icon: '🎫',
  },
  {
    id: 'invoices',
    label: 'Invoices',
    jobType: 'invoice_history',
    blurb: 'Invoice accessories onto tickets + standalone sales — run after tickets',
    stepKey: 'invoices',
    icon: '📄',
  },
];

function isAdmin(role: string | undefined): boolean {
  return role === 'tenant_admin' || role === 'platform_admin';
}

function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

function ProgressBar({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const blocks = 20;
  const filled = Math.round((pct / 100) * blocks);
  return (
    <div className="font-mono text-sm text-slate-800">
      <span className="text-primary">{'█'.repeat(filled)}</span>
      <span className="text-slate-300">{'░'.repeat(blocks - filled)}</span>
      <span className="ml-2 font-semibold">
        {done.toLocaleString()} / {total.toLocaleString()} ({pct}%)
      </span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'completed'
      ? 'bg-emerald-100 text-emerald-900'
      : status === 'failed' || status === 'invalid'
        ? 'bg-red-100 text-red-900'
        : status === 'cancelled'
          ? 'bg-slate-200 text-slate-800'
          : 'bg-amber-100 text-amber-950';
  return (
    <span className={cn('rounded-full px-2.5 py-0.5 text-xs font-bold uppercase', tone)}>
      {status}
    </span>
  );
}

function defaultOptions(jobType: string): MigrationImportOptions {
  const ticketPrefix = loadSavedTicketPrefix();
  switch (jobType) {
    case 'customers':
      return { onDuplicate: 'skip' };
    case 'inventory':
      return {
        onDuplicate: 'skip',
        createMissingCategories: true,
        createMissingSuppliers: true,
        createMissingDevices: true,
      };
    case 'services':
      return { onDuplicate: 'skip', createMissingDevices: true };
    case 'ticket_history':
      return { ticketPrefix, skipDuplicates: true };
    case 'invoice_history':
      return {
        ticketPrefix,
        salePrefix: loadSavedSalePrefix(),
        onDuplicate: 'skip',
        skipMissingTickets: true,
      };
    default:
      return {};
  }
}

export function MigrationSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { jobId: routeJobId } = useParams<{ jobId?: string }>();
  const allowed = isAdmin(user?.role);

  const [tab, setTab] = useState<TabId>('inventory');
  const [steps, setSteps] = useState<MigrationStepsState>(() => loadMigrationSteps());
  const [job, setJob] = useState<MigrationJob | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [stalled, setStalled] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [options, setOptions] = useState<MigrationImportOptions>(() =>
    defaultOptions('inventory'),
  );

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stallCountRef = useRef(0);
  const lastProcessedRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const [now, setNow] = useState(Date.now());

  const activeTab = TABS.find((t) => t.id === tab)!;
  const firstIncomplete = TABS.find((t) => !steps[t.stepKey])?.stepKey ?? null;

  function selectTab(next: TabId) {
    const idx = TABS.findIndex((t) => t.id === next);
    const item = TABS[idx];
    if (!item) return;
    if (idx > 0 && !steps[TABS[idx - 1]!.stepKey] && !steps[item.stepKey]) {
      toastError(`Import ${TABS[idx - 1]!.label} first for best results`);
    }
    if (isMigrationJobInProgress(job?.status ?? '')) return;
    setTab(next);
    if (!routeJobId) {
      setJob(null);
      setPanelError(null);
    }
  }

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const attachJob = useCallback(
    (next: MigrationJob) => {
      setJob(next);
      setPanelError(null);
      saveActiveMigrationJobId(next.id);
      if (routeJobId !== next.id) {
        navigate(`/settings/migration/${next.id}`, { replace: true });
      }
      const tabMatch = TABS.find((t) => t.jobType === next.jobType);
      if (tabMatch) setTab(tabMatch.id);
    },
    [navigate, routeJobId],
  );

  const pollOnce = useCallback(
    async (id: string) => {
      try {
        const next = await getMigrationJob(id);
        setJob(next);

        if (lastProcessedRef.current === next.processedRows) {
          stallCountRef.current += 1;
        } else {
          stallCountRef.current = 0;
          lastProcessedRef.current = next.processedRows;
        }

        if (stallCountRef.current >= MAX_STALL_POLLS && isMigrationJobInProgress(next.status)) {
          setStalled(true);
          stopPolling();
          setPanelError('Import may be stalled — no progress for 10 minutes.');
          return;
        }

        if (!isMigrationJobInProgress(next.status) && next.status !== 'validated') {
          stopPolling();
          if (next.status === 'completed') {
            clearActiveMigrationJobId();
            const tabMatch = TABS.find((t) => t.jobType === next.jobType);
            if (tabMatch) {
              setSteps(saveMigrationSteps({ [tabMatch.stepKey]: true }));
            }
            toastSuccess('Migration completed');
          }
        }
        if (next.status === 'validated') {
          stopPolling();
        }
      } catch (err) {
        const parsed = parseApiError(err);
        const status = (err as { response?: { status?: number } })?.response?.status;
        stopPolling();
        if (status === 404) {
          setJob(null);
          clearActiveMigrationJobId();
          setPanelError('Job not found');
          navigate('/settings/migration', { replace: true });
          return;
        }
        setPanelError(parsed.message);
        setStalled(true);
      }
    },
    [navigate, stopPolling],
  );

  const startPolling = useCallback(
    (id: string) => {
      stopPolling();
      setStalled(false);
      stallCountRef.current = 0;
      lastProcessedRef.current = null;
      startedAtRef.current = Date.now();
      void pollOnce(id);
      pollRef.current = window.setInterval(() => void pollOnce(id), POLL_MS);
    },
    [pollOnce, stopPolling],
  );

  // Reattach from URL or localStorage
  useEffect(() => {
    if (!allowed) return;
    const id = routeJobId || loadActiveMigrationJobId();
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await getMigrationJob(id);
        if (cancelled) return;
        attachJob(existing);
        setOptions((prev) => ({ ...defaultOptions(existing.jobType), ...existing.options, ...prev }));
        if (isMigrationJobInProgress(existing.status)) {
          startPolling(existing.id);
        }
      } catch {
        if (!cancelled) {
          clearActiveMigrationJobId();
          if (routeJobId) navigate('/settings/migration', { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // intentionally once on mount / route job change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, routeJobId]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    if (!job || !isMigrationJobInProgress(job.status)) return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [job]);

  useEffect(() => {
    setOptions(defaultOptions(activeTab.jobType));
  }, [activeTab.jobType]);

  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      setUploading(true);
      const timeout = new Promise<never>((_, reject) => {
        window.setTimeout(
          () => reject(new Error('Upload timed out after 30s')),
          UPLOAD_TIMEOUT_MS,
        );
      });
      const { jobId } = await Promise.race([
        uploadMigrationFile(file, activeTab.jobType),
        timeout,
      ]);
      // Pass prefix options at validate time (required for tickets/invoices matching)
      if (options.ticketPrefix) saveTicketPrefix(String(options.ticketPrefix));
      if (options.salePrefix) saveSalePrefix(String(options.salePrefix));
      await startMigrationValidate(jobId, options);
      return jobId;
    },
    onSuccess: (jobId) => {
      setUploading(false);
      toastSuccess('Upload received — validating…');
      saveActiveMigrationJobId(jobId);
      navigate(`/settings/migration/${jobId}`, { replace: true });
      startPolling(jobId);
    },
    onError: (e) => {
      setUploading(false);
      const parsed = parseApiError(e);
      setPanelError(parsed.message);
      toastError(parsed.message, parsed.code);
    },
  });

  const importMut = useMutation({
    mutationFn: async () => {
      if (!job) throw new Error('No job');
      if (options.ticketPrefix) saveTicketPrefix(String(options.ticketPrefix));
      if (options.salePrefix) saveSalePrefix(String(options.salePrefix));
      return startMigrationImport(job.id, options);
    },
    onSuccess: () => {
      toastSuccess('Import started');
      if (job) startPolling(job.id);
    },
    onError: (e) => {
      const parsed = parseApiError(e);
      setPanelError(parsed.message);
      toastError(parsed.message, parsed.code);
    },
  });

  const revalidateMut = useMutation({
    mutationFn: async () => {
      if (!job) throw new Error('No job');
      if (options.ticketPrefix) saveTicketPrefix(String(options.ticketPrefix));
      if (options.salePrefix) saveSalePrefix(String(options.salePrefix));
      return startMigrationValidate(job.id, options);
    },
    onSuccess: () => {
      setPanelError(null);
      toastSuccess('Re-validating with updated options…');
      if (job) startPolling(job.id);
    },
    onError: (e) => {
      const parsed = parseApiError(e);
      setPanelError(parsed.message);
      toastError(parsed.message, parsed.code);
    },
  });

  const resumeMut = useMutation({
    mutationFn: async () => {
      if (!job) throw new Error('No job');
      return resumeMigrationImport(job.id);
    },
    onSuccess: () => {
      setStalled(false);
      setPanelError(null);
      toastSuccess('Import resumed');
      if (job) startPolling(job.id);
    },
    onError: (e) => {
      const parsed = parseApiError(e);
      setPanelError(parsed.message);
      toastError(parsed.message, parsed.code);
    },
  });

  const cancelMut = useMutation({
    mutationFn: async () => {
      if (!job) throw new Error('No job');
      return cancelMigrationJob(job.id);
    },
    onSuccess: async () => {
      stopPolling();
      toastSuccess('Import cancelled');
      if (job) {
        const next = await getMigrationJob(job.id);
        setJob(next);
      }
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function onFile(file: File | undefined) {
    if (!file) return;
    setPanelError(null);
    setStalled(false);
    uploadMut.mutate(file);
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragOver(false);
    onFile(e.dataTransfer.files?.[0]);
  }

  const elapsedMs = useMemo(() => {
    if (!job?.startedAt) return startedAtRef.current ? now - startedAtRef.current : 0;
    return now - new Date(job.startedAt).getTime();
  }, [job?.startedAt, now]);

  const etaLabel = useMemo(() => {
    if (!job || job.processedRows <= 0 || job.totalRows <= 0) return null;
    const rate = job.processedRows / Math.max(1, elapsedMs / 1000);
    const remaining = Math.max(0, job.totalRows - job.processedRows);
    if (rate <= 0) return null;
    return formatElapsed((remaining / rate) * 1000);
  }, [elapsedMs, job]);

  if (!allowed) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-950">
        Only tenant admins can run data migrations.
      </div>
    );
  }

  const validation = job?.validationResult ?? null;
  const showOptions =
    job?.status === 'validated' ||
    (job?.status === 'invalid' &&
      (job.jobType === 'ticket_history' || job.jobType === 'invoice_history'));
  const showProgress =
    job &&
    ['validating', 'importing', 'completed', 'failed', 'cancelled'].includes(job.status);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-slate-950">RepairDesk Migration</h1>
        <p className="mt-1 text-sm text-slate-600">
          Import your data in this order. Upload → validate → import runs in the background;
          this page only polls status (no WebSockets).
        </p>
      </header>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
          Recommended import order
        </div>
        <ul className="divide-y divide-slate-100">
          {TABS.map((s, index) => {
            const done = steps[s.stepKey];
            const isCurrent =
              s.stepKey === firstIncomplete || (!firstIncomplete && s.id === tab);
            return (
              <li key={s.stepKey} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="w-16 font-bold text-slate-500">STEP {index + 1}</span>
                <span className="min-w-[8rem] font-semibold text-slate-900">
                  {s.icon} {s.label}
                </span>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => selectTab(s.id)}
                >
                  Import
                </button>
                <button
                  type="button"
                  className="rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-800"
                  title={done ? 'Mark as not done' : 'Mark as done'}
                  onClick={() =>
                    setSteps(saveMigrationSteps({ [s.stepKey]: !done }))
                  }
                >
                  {done ? 'Undo' : 'Mark done'}
                </button>
                <span
                  className="ml-auto text-lg"
                  title={done ? 'Imported' : isCurrent ? 'Next' : 'Pending'}
                >
                  {done ? '✅' : isCurrent ? '👈' : '○'}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectTab(t.id)}
            className={cn(
              'rounded-xl px-3 py-2 text-sm font-semibold',
              tab === t.id
                ? 'bg-primary text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
            )}
          >
            {t.label}
            {steps[t.stepKey] ? ' ✓' : ''}
          </button>
        ))}
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">{activeTab.label}</h2>
        <p className="mt-1 text-sm text-slate-600">{activeTab.blurb}</p>

        {tab === 'invoices' && !steps.tickets ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            <p className="font-semibold">Import tickets first</p>
            <p className="mt-1">
              Invoice accessories link to RepairDesk ticket numbers. Run the Tickets step before
              Invoices for accurate matching.
            </p>
            <button
              type="button"
              className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100"
              onClick={() => selectTab('tickets')}
            >
              Go to Tickets
            </button>
          </div>
        ) : null}

        {tab === 'tickets' || tab === 'invoices' ? (
          <div className="mt-4 space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900">Number prefixes</p>
            <p className="text-xs text-slate-600">
              Must match the prefix used when importing tickets (yours look like{' '}
              <span className="font-mono">RD-IPR1277</span>). Set this before upload — validation
              uses it to find tickets.
            </p>
            <label className="block text-sm">
              Ticket prefix
              <input
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                value={String(options.ticketPrefix ?? loadSavedTicketPrefix())}
                maxLength={20}
                onChange={(e) => {
                  const ticketPrefix = e.target.value;
                  saveTicketPrefix(ticketPrefix);
                  setOptions((o) => ({ ...o, ticketPrefix }));
                }}
              />
            </label>
            {tab === 'invoices' ? (
              <>
                <label className="block text-sm">
                  Sale / invoice prefix
                  <input
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                    value={String(options.salePrefix ?? loadSavedSalePrefix())}
                    maxLength={20}
                    onChange={(e) => {
                      const salePrefix = e.target.value;
                      saveSalePrefix(salePrefix);
                      setOptions((o) => ({ ...o, salePrefix }));
                    }}
                  />
                </label>
                <label className="block text-sm">
                  On duplicate
                  <select
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                    value={String(options.onDuplicate ?? 'skip')}
                    onChange={(e) =>
                      setOptions((o) => ({
                        ...o,
                        onDuplicate: e.target.value as 'skip' | 'update',
                      }))
                    }
                  >
                    <option value="skip">Skip already-imported invoices</option>
                    <option value="update">Update existing (refresh accessory / sale lines)</option>
                  </select>
                </label>
              </>
            ) : null}
          </div>
        ) : null}

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn(
            'mt-4 rounded-2xl border-2 border-dashed px-4 py-10 text-center transition',
            dragOver ? 'border-primary bg-primary/5' : 'border-slate-300 bg-slate-50',
          )}
        >
          <p className="text-sm font-medium text-slate-700">
            Drop RepairDesk export here, or choose a file
          </p>
          <p className="mt-1 text-xs text-slate-500">XLSX / CSV · max 50MB</p>
          <label className="mt-4 inline-flex cursor-pointer rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90">
            {uploading || uploadMut.isPending ? 'Uploading…' : 'Choose file'}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              disabled={uploading || uploadMut.isPending}
              onChange={(e) => onFile(e.target.files?.[0])}
            />
          </label>
        </div>

        {panelError ? (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {panelError}
            {job ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <Link to="/settings/migration" className="font-semibold underline">
                  Back to upload
                </Link>
              </div>
            ) : null}
          </div>
        ) : null}

        {job ? (
          <div className="mt-5 space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  Job <span className="font-mono text-xs">{job.id.slice(0, 8)}…</span>
                </p>
                <p className="text-xs text-slate-500">
                  {job.fileName ?? 'Uploaded file'}
                  {job.fileSize != null ? ` · ${(job.fileSize / 1024 / 1024).toFixed(1)} MB` : ''}
                </p>
              </div>
              <StatusBadge status={job.status} />
            </div>

            {job.status === 'validating' ? (
              <p className="text-sm text-slate-600">Validating file… polling every 2s</p>
            ) : null}

            {validation && (job.status === 'validated' || job.status === 'invalid') ? (
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-semibold">{validationRowCount(job).toLocaleString()}</span>{' '}
                  rows · {validation.warnings.length} warning(s) · {validation.errors.length}{' '}
                  error(s)
                </p>
                {typeof validation.summary?.ticketNotFound === 'number' &&
                validation.summary.ticketNotFound > 0 ? (
                  <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-950">
                    <span className="font-semibold">
                      {validation.summary.ticketNotFound.toLocaleString()} invoice(s)
                    </span>{' '}
                    could not match a ticket with the current prefix. You can still import —
                    unmatched accessory lines will be skipped. Adjust the prefix and re-validate if
                    needed.
                  </p>
                ) : null}
                {validation.warnings.slice(0, 8).map((w) => (
                  <p key={w} className="text-amber-800">
                    ⚠ {w}
                  </p>
                ))}
                {validation.errors.slice(0, 8).map((e: MigrationValidationError, i) => (
                  <p key={i} className="text-red-700">
                    {formatValidationError(e)}
                  </p>
                ))}
                {validation.preview.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                    <table className="min-w-full text-left text-xs">
                      <thead className="bg-slate-100 text-slate-600">
                        <tr>
                          {Object.keys(validation.preview[0] ?? {})
                            .slice(0, 8)
                            .map((k) => (
                              <th key={k} className="px-2 py-1.5 font-semibold">
                                {k}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {validation.preview.slice(0, 10).map((row, idx) => (
                          <tr key={idx} className="border-t border-slate-100">
                            {Object.keys(validation.preview[0] ?? {})
                              .slice(0, 8)
                              .map((k) => (
                                <td key={k} className="max-w-[10rem] truncate px-2 py-1">
                                  {String(row[k] ?? '')}
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            ) : null}

            {showOptions ? (
              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
                <h3 className="text-sm font-bold text-slate-900">Import options</h3>
                {job.jobType === 'customers' ||
                job.jobType === 'inventory' ||
                job.jobType === 'services' ||
                job.jobType === 'invoice_history' ? (
                  <label className="block text-sm">
                    On duplicate
                    <select
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
                      value={String(options.onDuplicate ?? 'skip')}
                      onChange={(e) =>
                        setOptions((o) => ({
                          ...o,
                          onDuplicate: e.target.value as 'skip' | 'update',
                        }))
                      }
                    >
                      <option value="skip">Skip existing</option>
                      <option value="update">
                        {job.jobType === 'services'
                          ? 'Update existing (merge device pricing)'
                          : job.jobType === 'invoice_history'
                            ? 'Update existing (refresh accessory / sale lines)'
                            : 'Update existing'}
                      </option>
                    </select>
                  </label>
                ) : null}
                {job.jobType === 'inventory' ? (
                  <div className="space-y-1 text-sm">
                    {(
                      [
                        ['createMissingCategories', 'Create missing categories'],
                        ['createMissingSuppliers', 'Create missing suppliers'],
                        ['createMissingDevices', 'Create missing devices'],
                      ] as const
                    ).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(options[key])}
                          onChange={(e) =>
                            setOptions((o) => ({ ...o, [key]: e.target.checked }))
                          }
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                ) : null}
                {job.jobType === 'services' ? (
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={options.createMissingDevices !== false}
                      onChange={(e) =>
                        setOptions((o) => ({ ...o, createMissingDevices: e.target.checked }))
                      }
                    />
                    Create missing devices
                  </label>
                ) : null}
                {job.jobType === 'ticket_history' || job.jobType === 'invoice_history' ? (
                  <>
                    <label className="block text-sm">
                      Ticket prefix
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                        value={String(options.ticketPrefix ?? 'RD-')}
                        maxLength={20}
                        onChange={(e) => {
                          const ticketPrefix = e.target.value;
                          saveTicketPrefix(ticketPrefix);
                          setOptions((o) => ({ ...o, ticketPrefix }));
                        }}
                      />
                    </label>
                    {job.jobType === 'invoice_history' ? (
                      <label className="block text-sm">
                        Sale / invoice prefix
                        <input
                          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-mono"
                          value={String(options.salePrefix ?? 'INV-')}
                          maxLength={20}
                          onChange={(e) => {
                            const salePrefix = e.target.value;
                            saveSalePrefix(salePrefix);
                            setOptions((o) => ({ ...o, salePrefix }));
                          }}
                        />
                      </label>
                    ) : null}
                  </>
                ) : null}
                {job.status === 'invalid' &&
                (job.jobType === 'ticket_history' || job.jobType === 'invoice_history') ? (
                  <button
                    type="button"
                    disabled={revalidateMut.isPending}
                    onClick={() => revalidateMut.mutate()}
                    className="rounded-xl border border-amber-600 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-950 disabled:opacity-50"
                  >
                    {revalidateMut.isPending
                      ? 'Re-validating…'
                      : 'Re-validate with this prefix →'}
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={importMut.isPending || job.status !== 'validated'}
                  onClick={() => importMut.mutate()}
                  className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {importMut.isPending ? 'Starting…' : 'Start import →'}
                </button>
              </div>
            ) : null}

            {showProgress ? (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-900">
                  {job.status === 'importing'
                    ? `Importing ${activeTab.label.toLowerCase()}…`
                    : job.status === 'validating'
                      ? 'Validating…'
                      : `Status: ${job.status}`}
                </p>
                <ProgressBar done={job.processedRows} total={Math.max(job.totalRows, 1)} />
                <p className="text-xs text-slate-600">
                  Elapsed {formatElapsed(elapsedMs)}
                  {etaLabel ? ` · ETA ~${etaLabel}` : ''}
                </p>
                <p className="text-sm text-slate-700">
                  Imported {job.importedCount.toLocaleString()} · Skipped{' '}
                  {job.skippedCount.toLocaleString()} · Errors {job.errorCount.toLocaleString()}
                </p>
                {job.errorMessage ? (
                  <p
                    className={
                      job.status === 'failed' || job.errorCount > 0
                        ? 'text-sm text-red-700'
                        : 'text-sm text-amber-800'
                    }
                  >
                    {job.errorMessage}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {job.status === 'importing' || stalled ? (
                    <button
                      type="button"
                      onClick={() => cancelMut.mutate()}
                      className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-semibold"
                    >
                      Cancel Import
                    </button>
                  ) : null}
                  {stalled || job.status === 'failed' ? (
                    <button
                      type="button"
                      onClick={() => resumeMut.mutate()}
                      disabled={resumeMut.isPending}
                      className="rounded-xl bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white"
                    >
                      Resume
                    </button>
                  ) : null}
                </div>
                {job.log?.length ? (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 font-mono text-[11px] text-slate-600">
                    {job.log.slice(-40).map((entry, i) => (
                      <div key={`${entry.at}-${i}`}>
                        [{entry.level}] {entry.message}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {!showProgress && !showOptions && !validation && job.status !== 'validating' ? (
              <p className="text-sm text-slate-600">
                Status <StatusBadge status={job.status} /> — use Resume if a prior import stalled,
                or upload a new file.
              </p>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
